import { supabase } from '@/integrations/supabase/client';

export type AlertType = 'below' | 'any_change' | 'sale';

export interface PriceAlert {
  id: string;
  user_id: string;
  product_id: string;
  target_price: number;
  alert_type: AlertType;
  is_active: boolean;
  last_notified_at: string | null;
  created_at: string;
  product?: {
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    image: string | null;
    price: number | null;
    current_price: number | null;
  };
}

/**
 * Get user's price alerts with product details
 */
export async function getPriceAlerts(userId: string): Promise<PriceAlert[]> {
  const { data, error } = await supabase
    .from('price_alerts')
    .select(`
      *,
      product:products(
        id,
        slug,
        title,
        subtitle,
        image,
        price,
        current_price
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as PriceAlert[];
}

/**
 * Create a price alert
 */
export async function createPriceAlert(params: {
  userId: string;
  productId: string;
  targetPrice: number;
  alertType?: AlertType;
}) {
  const { data, error } = await supabase
    .from('price_alerts')
    .insert({
      user_id: params.userId,
      product_id: params.productId,
      target_price: params.targetPrice,
      alert_type: params.alertType || 'below',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update a price alert
 */
export async function updatePriceAlert(
  alertId: string,
  updates: {
    target_price?: number;
    alert_type?: AlertType;
    is_active?: boolean;
  }
) {
  const { data, error } = await supabase
    .from('price_alerts')
    .update(updates)
    .eq('id', alertId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete a price alert
 */
export async function deletePriceAlert(alertId: string) {
  const { error } = await supabase
    .from('price_alerts')
    .delete()
    .eq('id', alertId);

  if (error) throw error;
}

/**
 * Check if user has alert for product
 */
export async function hasAlertForProduct(
  userId: string,
  productId: string
): Promise<PriceAlert | null> {
  const { data, error } = await supabase
    .from('price_alerts')
    .select('*')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data as PriceAlert | null;
}

/**
 * Get price history for a product
 */
export async function getPriceHistory(productId: string, limit = 30) {
  const { data, error } = await supabase
    .from('price_history')
    .select('*')
    .eq('product_id', productId)
    .order('recorded_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}
